import { useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { X, Plus, Save, Loader2, Lock, AlertTriangle } from "lucide-react";
import { MDR_BASE } from "../../lib/claimMdr";

/**
 * Daftar Leasing / CC (brief 22 Sep butir 8–11).
 *
 *   "buatkan tombol daftar leasing yang bisa saya pilih custom mana leasing
 *    untuk tam dan gdn"
 *
 * DUA hal yang TIDAK bisa diubah dari sini, dan keduanya aturan vendor:
 *  - CC BRI/BCA/Mandiri hanya GDN (butir 9 & 10 menyebutnya dua kali). Layar
 *    mematikan centang TAM-nya DAN server memaksanya lagi saat disimpan —
 *    gerbang layar saja bisa ditembus lewat API.
 *  - `prioritas` menentukan JALUR pencocokan (butir 12 vs 14), bukan tampilan,
 *    jadi ia hanya ditampilkan untuk leasing bawaan.
 *
 * Yang ditampilkan sesudah menyimpan adalah daftar yang BENAR-BENAR tersimpan
 * (balasan server, hasil normalisasi) — bukan kiriman layar. Kalau ada pilihan
 * yang dipaksa, itu langsung terlihat alih-alih tampak berhasil.
 */

// Kode yang tidak boleh diproses di TAM. Diketik di sini HANYA untuk mematikan
// centangnya lebih awal (aturan 3: jangan tawarkan yang ujungnya ditolak);
// penegakannya tetap di `leasing.HANYA_GDN` di server.
const HANYA_GDN = ["cc_bri", "cc_bca", "cc_mandiri"];

export default function ClaimMdrLeasingModal({ tab = [], leasing = [], onTutup, onSimpan }) {
  const [rows, setRows] = useState(() => leasing.map((l) => ({ ...l, tab: [...(l.tab || [])] })));
  const [sibuk, setSibuk] = useState(false);
  const [dipaksa, setDipaksa] = useState([]);
  const [baru, setBaru] = useState({ kode: "", nama: "", prioritas: false });

  const labelTab = useMemo(
    () => Object.fromEntries(tab.map((t) => [t.kode, t.label])), [tab]);

  const setTab = (kode, kodeTab, on) => setRows((rs) => rs.map((r) => {
    if (r.kode !== kode) return r;
    const t = new Set(r.tab || []);
    if (on) t.add(kodeTab); else t.delete(kodeTab);
    return { ...r, tab: [...t] };
  }));

  const setNonaktif = (kode, on) =>
    setRows((rs) => rs.map((r) => (r.kode === kode ? { ...r, nonaktif: on } : r)));

  const tambah = () => {
    const kode = baru.kode.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    const nama = baru.nama.trim();
    if (!kode || !nama) { toast.error("Kode dan nama leasing wajib diisi 😊"); return; }
    if (rows.some((r) => r.kode === kode)) { toast.error(`Kode "${kode}" sudah ada`); return; }
    setRows((rs) => [...rs, { kode, nama, prioritas: baru.prioritas,
                              tab: tab.map((t) => t.kode), tambahan: true }]);
    setBaru({ kode: "", nama: "", prioritas: false });
  };

  const simpan = async () => {
    const kosong = rows.filter((r) => !r.nonaktif && !(r.tab || []).length);
    if (kosong.length) {
      toast.error(`Pilih minimal satu tab untuk: ${kosong.map((r) => r.nama).join(", ")}`);
      return;
    }
    setSibuk(true); setDipaksa([]);
    try {
      const r = await api.put(`${MDR_BASE}/leasing`, {
        leasing: rows.map((x) => ({ kode: x.kode, nama: x.nama, prioritas: !!x.prioritas,
                                    tab: x.tab || [], nonaktif: !!x.nonaktif })),
      });
      const tersimpan = r.data.items || [];
      // Bandingkan KIRIMAN dengan yang TERSIMPAN: kalau server memaksa (CC
      // kembali ke GDN), Finance harus melihatnya, bukan mengira pilihannya
      // berlaku lalu bingung kenapa barisnya tidak pernah masuk tab TAM.
      const per = Object.fromEntries(tersimpan.map((x) => [x.kode, x]));
      const beda = rows
        .filter((x) => per[x.kode] &&
          [...(x.tab || [])].sort().join(",") !== [...(per[x.kode].tab || [])].sort().join(","))
        .map((x) => ({ nama: x.nama, jadi: (per[x.kode].tab || []) }));
      setDipaksa(beda);
      setRows(tersimpan.map((l) => ({ ...l, tab: [...(l.tab || [])] })));
      onSimpan?.(tersimpan);
      if (beda.length) toast.warning(`${beda.length} leasing disesuaikan aturan vendor`);
      else toast.success("Daftar leasing disimpan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan daftar leasing");
    } finally { setSibuk(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 p-4 overflow-y-auto"
      data-testid="mdr-leasing-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-8">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Daftar Leasing / CC</h2>
          <button onClick={onTutup} data-testid="mdr-leasing-tutup"
            className="p-1 rounded hover:bg-slate-100 text-slate-500" aria-label="Tutup">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-xs text-slate-500">
            Pilih di tab mana tiap leasing diproses. CC BRI/BCA/Mandiri hanya bisa di{" "}
            {labelTab.gdn || "GDN"} — itu aturan vendor, bukan pengaturan.
          </p>

          {dipaksa.length > 0 && (
            <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg"
              data-testid="mdr-leasing-dipaksa">
              <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> Disesuaikan aturan vendor
              </p>
              <ul className="mt-0.5">
                {dipaksa.map((d) => (
                  <li key={d.nama} className="text-[11px] text-amber-700">
                    {d.nama} → {d.jadi.map((k) => labelTab[k] || k).join(" & ") || "tidak ada tab"}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Leasing / CC</th>
                  <th className="px-3 py-2 text-left font-medium">Jalur</th>
                  {tab.map((t) => (
                    <th key={t.kode} className="px-3 py-2 text-center font-medium">{t.label}</th>
                  ))}
                  <th className="px-3 py-2 text-center font-medium">Aktif</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => {
                  const ccGdn = HANYA_GDN.includes(r.kode);
                  return (
                    <tr key={r.kode} data-testid={`mdr-leasing-${r.kode}`}
                      className={r.nonaktif ? "text-slate-400" : ""}>
                      <td className="px-3 py-2">
                        {r.nama}
                        {r.tambahan && <span className="ml-1.5 text-[10px] text-slate-400">tambahan</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        {r.prioritas ? "Prioritas" : "Non-prioritas"}
                      </td>
                      {tab.map((t) => {
                        const mati = ccGdn && t.kode === "tam";
                        return (
                          <td key={t.kode} className="px-3 py-2 text-center">
                            <input type="checkbox" checked={(r.tab || []).includes(t.kode)}
                              disabled={mati || r.nonaktif}
                              title={mati ? "CC hanya diproses di GDN (butir 9)" : ""}
                              onChange={(e) => setTab(r.kode, t.kode, e.target.checked)}
                              data-testid={`mdr-leasing-${r.kode}-${t.kode}`}
                              aria-label={`${r.nama} di ${t.label}`} />
                            {mati && <Lock className="w-3 h-3 inline-block ml-1 text-slate-300" />}
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 text-center">
                        <input type="checkbox" checked={!r.nonaktif}
                          onChange={(e) => setNonaktif(r.kode, !e.target.checked)}
                          data-testid={`mdr-leasing-${r.kode}-aktif`}
                          aria-label={`${r.nama} aktif`} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Tambah leasing baru (butir 8). Leasing bawaan tidak bisa dihapus —
              hanya dinonaktifkan — karena kodenya dipakai data yang sudah ada. */}
          <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-slate-200">
            <div>
              <label className="block text-[11px] text-slate-500 mb-0.5">Kode</label>
              <input value={baru.kode} onChange={(e) => setBaru((b) => ({ ...b, kode: e.target.value }))}
                placeholder="mis. home_credit" data-testid="mdr-leasing-baru-kode"
                className="px-2 py-1.5 rounded-lg border border-slate-200 text-sm w-40" />
            </div>
            <div>
              <label className="block text-[11px] text-slate-500 mb-0.5">Nama tampil</label>
              <input value={baru.nama} onChange={(e) => setBaru((b) => ({ ...b, nama: e.target.value }))}
                placeholder="mis. Home Credit" data-testid="mdr-leasing-baru-nama"
                className="px-2 py-1.5 rounded-lg border border-slate-200 text-sm w-44" />
            </div>
            <label className="flex items-center gap-1.5 text-xs text-slate-600 pb-2">
              <input type="checkbox" checked={baru.prioritas}
                onChange={(e) => setBaru((b) => ({ ...b, prioritas: e.target.checked }))}
                data-testid="mdr-leasing-baru-prioritas" />
              Prioritas
            </label>
            <button onClick={tambah} data-testid="mdr-leasing-tambah"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50">
              <Plus className="w-3.5 h-3.5" /> Tambah
            </button>
          </div>
          <p className="text-[11px] text-slate-400">
            Leasing bawaan tidak bisa dihapus — kodenya dipakai Data Claim yang sudah masuk.
            Matikan centang “Aktif” supaya tidak lagi dipakai pencocokan baru.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <button onClick={onTutup} data-testid="mdr-leasing-batal"
            className="px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">
            Tutup
          </button>
          <button onClick={simpan} disabled={sibuk} data-testid="mdr-leasing-simpan"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50">
            {sibuk ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Simpan
          </button>
        </div>
      </div>
    </div>
  );
}
